/**
 * My subscriptions — dashboard content (report-subscriptions spec, Req 2).
 *
 * Lists the signed-in user's report subscriptions across all teams (title →
 * the report's deep link, team, cadence, last digest outcome) with a cadence
 * picker (weekly / daily; switching goes through subscribeToReport with the
 * report's team) and Unsubscribe per row, a "Show ended" toggle that lists
 * ended subscriptions with their reason and Resubscribe (same cadence) when
 * the report is still subscribable, and the
 * `?unsubscribe=<id>` deep link every digest email carries: consumed once on
 * mount (a ref guards StrictMode's double effect), removed from the address
 * bar, acted on through unsubscribeFromReport, and confirmed with an Undo.
 * The resolver only ever acts on the caller's own rows, so a foreign id is an
 * AuthorizationError shown as "This unsubscribe link is not valid for your
 * account". The link survives the Midway sign-in round trip through App's
 * postAuthRedirect (Req 2.4).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { generateAuditedClient } from "../../../../audit/auditedClient";
import { getMyReportSubscriptions } from "../../../../graphql/queries";
import {
  subscribeToReport,
  unsubscribeFromReport,
} from "../../../../graphql/mutations";
import type {
  GetMyReportSubscriptionsInput,
  ReportSubscription,
  SubscribeToReportInput,
  UnsubscribeFromReportInput,
} from "../../../../models/API";
import type { PluginReportContentProps } from "../../types";
import { covertUtcToLocal } from "../../../utils/formatters";
import { publishedReportDeepLink } from "../../../publishedReports/publishedReportModel";
import {
  cadenceLabel,
  cadenceOf,
  canChangeCadence,
  canResubscribe,
  clearUnsubscribeParam,
  deliveryLabel,
  DIGEST_CADENCES,
  DIGEST_TIME_LABEL,
  digestDescription,
  endedReasonLabel,
  readUnsubscribeParam,
  scheduleLabel,
  subscriptionTitle,
} from "../../../publishedReports/reportSubscriptionModel";
import type { DigestCadence } from "../../../publishedReports/reportSubscriptionModel";

interface Notice {
  severity: "info" | "success" | "warning" | "error";
  text: string;
  /** Resubscribe the row this notice is about. */
  undo?: ReportSubscription;
}

function errorMessageOf(err: any, fallback: string): string {
  const message = err?.errors?.[0]?.message ?? err?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

function errorTypeOf(err: any): string | undefined {
  const type = err?.errors?.[0]?.errorType;
  return typeof type === "string" ? type : undefined;
}

const MySubscriptionsContent: React.FC<PluginReportContentProps> = ({
  userId,
}) => {
  const client = useMemo(() => generateAuditedClient(), []);
  const [rows, setRows] = useState<ReportSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showEnded, setShowEnded] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  // The deep link is acted on once per mount.
  const unsubscribeHandledRef = useRef(false);

  const ready = userId !== undefined;

  const load = useCallback(
    async (includeEnded: boolean) => {
      if (!ready) return;
      setLoading(true);
      setLoadError(null);
      try {
        const input: GetMyReportSubscriptionsInput = {
          user_id: userId!,
          include_ended: includeEnded,
        };
        const result = await client.graphql({
          query: getMyReportSubscriptions,
          variables: { input },
        });
        setRows(result.data.getMyReportSubscriptions ?? []);
      } catch (err) {
        console.error("[MySubscriptions] list failed:", err);
        setLoadError(
          errorMessageOf(
            err,
            "Could not load your subscriptions. Please try again."
          )
        );
      } finally {
        setLoading(false);
      }
    },
    [client, ready, userId]
  );

  useEffect(() => {
    load(showEnded);
  }, [load, showEnded]);

  const unsubscribe = useCallback(
    async (
      input: Omit<UnsubscribeFromReportInput, "user_id">
    ): Promise<ReportSubscription> => {
      const result = await client.graphql({
        query: unsubscribeFromReport,
        variables: { input: { user_id: userId!, ...input } },
      });
      const row = result.data.unsubscribeFromReport;
      if (!row) throw new Error("No subscription returned");
      return row;
    },
    [client, userId]
  );

  // Subscribe again (Resubscribe, Undo) or switch the cadence: the report's
  // own team keeps it inside the F1 read scope whichever team is selected.
  const subscribe = useCallback(
    async (
      row: ReportSubscription,
      cadence: DigestCadence
    ): Promise<ReportSubscription> => {
      if (typeof row.report_team_id !== "number")
        throw new Error("This report is no longer available");
      const input: SubscribeToReportInput = {
        user_id: userId!,
        team_id: row.report_team_id,
        report_id: row.report_id,
        cadence,
      };
      const result = await client.graphql({
        query: subscribeToReport,
        variables: { input },
      });
      const created = result.data.subscribeToReport;
      if (!created) throw new Error("No subscription returned");
      return created;
    },
    [client, userId]
  );

  // The email's unsubscribe link (Req 2.3).
  useEffect(() => {
    if (!ready || unsubscribeHandledRef.current) return;
    const id = readUnsubscribeParam();
    if (id === null) return;
    unsubscribeHandledRef.current = true;
    clearUnsubscribeParam();
    (async () => {
      try {
        const row = await unsubscribe({ subscription_id: id });
        const title = subscriptionTitle(row);
        if (row.was_active === false) {
          setNotice({
            severity: "info",
            text: `Already unsubscribed from "${title}".`,
          });
        } else {
          setNotice({
            severity: "success",
            text: `Unsubscribed from "${title}".`,
            undo: canResubscribe({ ...row, active: false }) ? row : undefined,
          });
        }
        load(showEnded);
      } catch (err) {
        console.error("[MySubscriptions] unsubscribe link failed:", err);
        // The resolver rejects a subscription that is not the caller's with an
        // AuthorizationError ("Subscription not found for your account"); match
        // either signal so the notice does not depend on how Amplify surfaces it.
        const foreign =
          errorTypeOf(err) === "AuthorizationError" ||
          /not found for your account/i.test(errorMessageOf(err, ""));
        setNotice({
          severity: "warning",
          text: foreign
            ? "This unsubscribe link is not valid for your account."
            : errorMessageOf(err, "Could not unsubscribe. Please try again."),
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const handleUnsubscribe = async (row: ReportSubscription) => {
    setBusyId(row.id);
    setNotice(null);
    try {
      const ended = await unsubscribe({ subscription_id: row.id });
      setNotice({
        severity: "success",
        text: `Unsubscribed from "${subscriptionTitle(ended)}".`,
        undo: canResubscribe({ ...ended, active: false }) ? ended : undefined,
      });
      await load(showEnded);
    } catch (err) {
      console.error("[MySubscriptions] unsubscribe failed:", err);
      setNotice({
        severity: "error",
        text: errorMessageOf(err, "Could not unsubscribe. Please try again."),
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleResubscribe = async (row: ReportSubscription) => {
    setBusyId(row.id);
    setNotice(null);
    try {
      const created = await subscribe(row, cadenceOf(row.cadence));
      setNotice({
        severity: "success",
        text: `Subscribed to "${subscriptionTitle(
          created
        )}". Digests go out ${scheduleLabel(created.cadence)}.`,
      });
      await load(showEnded);
    } catch (err) {
      console.error("[MySubscriptions] resubscribe failed:", err);
      setNotice({
        severity: "error",
        text: errorMessageOf(err, "Could not subscribe. Please try again."),
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleCadenceChange = async (
    row: ReportSubscription,
    cadence: DigestCadence
  ) => {
    if (cadenceOf(row.cadence) === cadence) return;
    setBusyId(row.id);
    setNotice(null);
    try {
      const changed = await subscribe(row, cadence);
      setNotice({
        severity: "success",
        text: `Switched "${subscriptionTitle(changed)}" to ${digestDescription(
          changed.cadence
        )}. The change applies from the next send time.`,
      });
      await load(showEnded);
    } catch (err) {
      console.error("[MySubscriptions] cadence change failed:", err);
      setNotice({
        severity: "error",
        text: errorMessageOf(
          err,
          "Could not change the cadence. Please try again."
        ),
      });
    } finally {
      setBusyId(null);
    }
  };

  const active = rows.filter((r) => r.active);
  const ended = rows.filter((r) => !r.active);

  const renderTitle = (row: ReportSubscription) => {
    if (row.report_title === null || row.report_title === undefined) {
      return (
        <Typography variant="body2" color="text.secondary">
          Report no longer available
        </Typography>
      );
    }
    return (
      <Link
        href={publishedReportDeepLink({
          id: row.report_id,
          team_name: row.report_is_global ? null : row.report_team_name,
        })}
        variant="body2"
        fontWeight={600}
        underline="hover"
      >
        {row.report_title}
      </Link>
    );
  };

  const renderRows = (list: ReportSubscription[], isEnded: boolean) =>
    list.map((row) => (
      <TableRow key={row.id} hover data-testid={`subscription-${row.id}`}>
        <TableCell sx={{ maxWidth: 360 }}>{renderTitle(row)}</TableCell>
        <TableCell>{row.report_team_name ?? "—"}</TableCell>
        <TableCell>
          {!isEnded && canChangeCadence(row) ? (
            <Select
              size="small"
              variant="standard"
              disableUnderline
              value={cadenceOf(row.cadence)}
              onChange={(e) =>
                handleCadenceChange(row, e.target.value as DigestCadence)
              }
              disabled={busyId === row.id}
              inputProps={{ "aria-label": `Cadence of ${row.report_title}` }}
              sx={{ typography: "body2" }}
            >
              {DIGEST_CADENCES.map((cadence) => (
                <MenuItem key={cadence} value={cadence}>
                  {cadenceLabel(cadence)}
                </MenuItem>
              ))}
            </Select>
          ) : (
            cadenceLabel(row.cadence)
          )}
        </TableCell>
        <TableCell>
          {isEnded ? (
            <Chip
              size="small"
              variant="outlined"
              label={endedReasonLabel(row.ended_reason)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {deliveryLabel(row, covertUtcToLocal)}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          {isEnded ? (
            canResubscribe(row) && (
              <Button
                size="small"
                onClick={() => handleResubscribe(row)}
                disabled={busyId === row.id}
              >
                Resubscribe
              </Button>
            )
          ) : (
            <Button
              size="small"
              color="inherit"
              onClick={() => handleUnsubscribe(row)}
              disabled={busyId === row.id}
            >
              Unsubscribe
            </Button>
          )}
        </TableCell>
      </TableRow>
    ));

  return (
    <Box data-testid="my-subscriptions">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          Weekly digests go out on Mondays and daily digests every day, at{" "}
          {DIGEST_TIME_LABEL}. Subscribe with the bell on any refreshable report
          in Published Reports.
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showEnded}
                onChange={(e) => setShowEnded(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Show ended</Typography>}
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={() => load(showEnded)}
                disabled={loading}
                aria-label="refresh subscriptions"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {notice && (
        <Alert
          severity={notice.severity}
          onClose={() => setNotice(null)}
          sx={{ mb: 1 }}
          action={
            notice.undo ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  const row = notice.undo!;
                  setNotice(null);
                  handleResubscribe(row);
                }}
              >
                Undo
              </Button>
            ) : undefined
          }
        >
          {notice.text}
        </Alert>
      )}
      {loadError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {loadError}
        </Alert>
      )}

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading subscriptions…</Typography>
        </Box>
      ) : active.length === 0 && (!showEnded || ended.length === 0) ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {showEnded
            ? "No subscriptions yet."
            : "No active subscriptions. Open a refreshable report in Published Reports and use the bell to subscribe."}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Report</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Cadence</TableCell>
              <TableCell>Last digest</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {renderRows(active, false)}
            {showEnded && ended.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={5} sx={{ bgcolor: "action.hover" }}>
                    <Typography variant="caption" color="text.secondary">
                      Ended subscriptions
                    </Typography>
                  </TableCell>
                </TableRow>
                {renderRows(ended, true)}
              </>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default MySubscriptionsContent;
